import SEO from '../components/SEO';
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, User, MessageSquare } from "lucide-react";
import Navigation from '../components/Navigation';
import { API, TURNSTILE_SITE_KEY } from "@/config";
import { usePageTitle } from "@/hooks/usePageTitle";
import SiteFooter from '../components/SiteFooter';

const ContactPage = () => {
  usePageTitle('Contact Us');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  useEffect(() => {
    let widgetId = null;
    const renderWidget = () => {
      if (TURNSTILE_SITE_KEY && window.turnstile && turnstileRef.current) {
        turnstileRef.current.innerHTML = '';
        widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          theme: 'light'
        });
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          renderWidget();
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      toast.error('Please complete the verification');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        turnstile_token: turnstileToken
      });
      toast.success('Message sent! We\'ll get back to you soon.');
      alert('✅ Thank you! Your message has been sent successfully.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTurnstileToken('');
      window.turnstile?.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO title="Contact Us" description="Questions about credit card benefits, 401k matching, or employee perks? Our benefits experts with 30+ years experience are here to help." path="/contact" keywords="benefits advisor, employee benefits help, benefits consultation, credit card benefits expert" />
      
      <Navigation />
      
      {/* Contact Header */}
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Questions? We're here to help you maximize your benefits.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-10 md:py-16 bg-gray-50 flex-grow">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User size={14} className="inline mr-2" />
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Your name"
                  className="w-full bg-white border border-gray-300 px-4 py-3 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 outline-none transition-colors text-gray-900"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={14} className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="your@email.com"
                  className="w-full bg-white border border-gray-300 px-4 py-3 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 outline-none transition-colors text-gray-900"
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="What's this about?"
                  className="w-full bg-white border border-gray-300 px-4 py-3 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 outline-none transition-colors text-gray-900"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare size={14} className="inline mr-2" />
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  placeholder="Your message..."
                  rows={6}
                  className="w-full bg-white border border-gray-300 px-4 py-3 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 outline-none transition-colors resize-none text-gray-900"
                />
              </div>

              {/* Turnstile */}
              <div className="flex justify-center">
                <div ref={turnstileRef} className="cf-turnstile"></div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !turnstileToken}
                className="w-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 px-6 py-4 rounded-lg font-bold transition-all shadow-lg flex items-center justify-center gap-2"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      
      <SiteFooter />
    </div>
  );
};

export default ContactPage;
